import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { Amount } from "@/components/shared/Amount";
import { AHeader, ACard, ATable } from "@/components/shared/AdminKit";
import { StatusBadge } from "@/components/shared/Badges";
import { fmtDate } from "@/components/shared/DashKit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, AlarmClock, Check, Undo2, ExternalLink } from "lucide-react";

const THRESHOLDS = [5, 10, 15, 30, 60];
const waitLabel = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}min` : `${m} min`);

export default function AdminPaymentAlerts() {
  const { t: tr } = useContent();
  const [data, setData] = useState({ alerts: [], unhandled_count: 0, threshold: 15 });
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(15);
  const [busy, setBusy] = useState(null);
  const nav = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/payment-alerts?threshold=${threshold}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [threshold]);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const openTxn = (a) => {
    if (a.panel === "pawapay") nav("/admin/pawapay");
    else if (a.panel) nav(`/admin/${a.panel}?q=${encodeURIComponent(a.ref)}`);
  };

  const setHandled = async (a, handled) => {
    setBusy(a.payment_id);
    try {
      await api.post(`/admin/payment-alerts/${a.payment_id}/handle`, { handled });
      toast.success(handled ? tr("payalerts.handled_toast", "Alerte marquée traitée") : tr("payalerts.reopened_toast", "Alerte rouverte"));
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <AHeader title={tr("payalerts.title", "Alertes — Paiements en attente")}
        subtitle={tr("payalerts.subtitle", "Paiements bloqués au-delà du délai choisi (PawaPay, Carte Bancaire, USDT). Lecture seule — n'affecte aucun paiement.")}
        action={
          <Select value={String(threshold)} onValueChange={(v) => setThreshold(Number(v))}>
            <SelectTrigger data-testid="alerts-threshold" className="w-44 border-slate-700 bg-slate-800 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>{THRESHOLDS.map((t) => <SelectItem key={t} value={String(t)}>{tr("payalerts.wait_gt", "En attente >")} {t} min</SelectItem>)}</SelectContent>
          </Select>} />

      <ACard>
        <div className="mb-3 flex items-center gap-2 text-sm text-slate-300" data-testid="alerts-summary">
          <AlarmClock className="h-4 w-4 text-accent" />
          <span><span className="font-semibold text-white">{data.unhandled_count}</span> {tr("payalerts.summary", "alerte(s) non traitée(s) · seuil")} {data.threshold} min</span>
        </div>
        {loading ? <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>
          : (data.alerts || []).length === 0 ? <div className="py-10 text-center text-slate-400" data-testid="alerts-empty">{tr("payalerts.empty", "Aucun paiement en attente au-delà du seuil. 🎉")}</div>
          : (
            <ATable head={[tr("payalerts.col.ref", "Réf."), tr("payalerts.col.client", "Client"), tr("payalerts.col.amount", "Montant"), tr("payalerts.col.method", "Méthode"), tr("payalerts.col.wait", "Attente"), tr("payalerts.col.status", "Statut"), tr("payalerts.col.created", "Créé le"), tr("payalerts.col.state", "État"), ""]}>
              {data.alerts.map((a) => (
                <tr key={a.payment_id} className={`border-b border-slate-800/60 last:border-0 ${a.handled ? "opacity-50" : ""}`} data-testid={`alert-row-${a.payment_id}`}>
                  <td className="px-3 py-3">
                    <button onClick={() => openTxn(a)} data-testid={`alert-open-${a.payment_id}`} className="inline-flex items-center gap-1 font-mono text-xs text-accent hover:underline">
                      {a.ref} <ExternalLink className="h-3 w-3" />
                    </button>
                  </td>
                  <td className="px-3 py-3 text-slate-400">{a.user_email}</td>
                  <td className="px-3 py-3 font-medium"><Amount value={a.amount} /></td>
                  <td className="px-3 py-3 text-slate-300">{a.method_label}</td>
                  <td className="px-3 py-3"><span className={`rounded-full px-2 py-0.5 text-xs ${a.wait_minutes >= 60 ? "bg-red-500/15 text-red-300" : "bg-amber-500/15 text-amber-300"}`}>{waitLabel(a.wait_minutes)}</span></td>
                  <td className="px-3 py-3"><StatusBadge status={a.status} /></td>
                  <td className="px-3 py-3 text-slate-400">{fmtDate(a.created_at)}</td>
                  <td className="px-3 py-3">{a.handled ? <span className="text-xs text-emerald-400">{tr("payalerts.handled", "Traitée")}</span> : <span className="text-xs text-amber-400">{tr("payalerts.unhandled", "Non traitée")}</span>}</td>
                  <td className="px-3 py-3">
                    {a.handled
                      ? <button onClick={() => setHandled(a, false)} disabled={busy === a.payment_id} data-testid={`alert-reopen-${a.payment_id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-2.5 py-1 text-xs text-slate-200 hover:bg-slate-800 disabled:opacity-50"><Undo2 className="h-3 w-3" /> {tr("payalerts.reopen", "Rouvrir")}</button>
                      : <button onClick={() => setHandled(a, true)} disabled={busy === a.payment_id} data-testid={`alert-handle-${a.payment_id}`} className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-xs text-white hover:opacity-90 disabled:opacity-50"><Check className="h-3 w-3" /> {tr("payalerts.handle", "Traiter")}</button>}
                  </td>
                </tr>
              ))}
            </ATable>
          )}
      </ACard>
    </div>
  );
}
